/**
 * Medallion — a tinted glyph disc: an icon centred in a tone-washed tile or
 * circle. The web twin of mobile `primitives/Medallion`, same props.
 *
 * One platform difference, on purpose: here the medallion also sizes and inks
 * the glyph. A heroicon fills its box and draws in `currentColor`, so callers
 * pass a bare icon; mobile's `Glyph` takes explicit size and colour props, so
 * there the caller supplies them.
 *
 * Decorative — the text beside it says what it means — so it is hidden from
 * assistive tech. A photo of a person is an `Avatar`, not a medallion.
 *
 * @example
 * <Medallion icon={<TrophyIcon />} />
 * <Medallion icon={<SparklesIcon />} tone="accent" shape="circle" />
 */

import type {
  MedallionProps,
  MedallionShape,
  MedallionSize,
  MedallionTone,
} from './Medallion.types';

const SIZE_CLASSES: Record<MedallionSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

/** Glyph box per disc size — the proportion the notification medallions use. */
const GLYPH_CLASSES: Record<MedallionSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

/** A tile's corner grows with the disc, so every size reads as the same shape. */
const TILE_RADIUS_CLASSES: Record<MedallionSize, string> = {
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
};

const TONE_CLASSES: Record<MedallionTone, string> = {
  neutral: 'bg-lift-2 text-neutral-300',
  accent: 'bg-sakura-wash text-primary-300',
  success: 'bg-success-wash text-success-400',
  warning: 'bg-warning-wash text-warning-400',
  error: 'bg-error-wash text-error-400',
  info: 'bg-info-wash text-info-400',
};

function radiusClass(shape: MedallionShape, size: MedallionSize): string {
  return shape === 'circle' ? 'rounded-full' : TILE_RADIUS_CLASSES[size];
}

export function Medallion({
  icon,
  size = 'md',
  tone = 'neutral',
  shape = 'tile',
  className = '',
  'data-testid': testId,
}: MedallionProps): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      className={[
        'inline-flex shrink-0 items-center justify-center',
        SIZE_CLASSES[size],
        radiusClass(shape, size),
        TONE_CLASSES[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        className={`flex shrink-0 items-center justify-center ${GLYPH_CLASSES[size]}`}
      >
        {icon}
      </span>
    </span>
  );
}

export default Medallion;
